import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Plus, Pencil, Trash2, Calendar, MapPin, ExternalLink, Star } from 'lucide-react';
import ImageUploader from '../../components/admin/ImageUploader';
import { getImageUrl } from '../../utils/imageUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminEventsPage = () => {
  const [events, setEvents] = useState([]);
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'
  const [editingEvent, setEditingEvent] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    id: '',
    title: '',
    subtitle: '',
    date: '',
    time: '',
    venue: '',
    address: '',
    city: '',
    type: 'monthly',
    xceed_url: '',
    poster_url: '',
    status: 'upcoming',
    featured: false
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  // Re-sort when sort order changes
  useEffect(() => {
    if (events.length > 0) {
      const sortedEvents = [...events].sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
      });
      setEvents(sortedEvents);
    }
  }, [sortOrder]);

  const fetchEvents = async () => {
    const auth = localStorage.getItem('admin_auth');
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/events`, {
        headers: { Authorization: `Basic ${auth}` }
      });
      const fetchedEvents = response.data.events || [];
      // Sort events by date
      const sortedEvents = [...fetchedEvents].sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
      });
      setEvents(sortedEvents);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const handleSubmit = async () => {
    const auth = localStorage.getItem('admin_auth');
    try {
      if (editingEvent) {
        await axios.put(`${BACKEND_URL}/api/admin/events/${editingEvent.id}`, formData, {
          headers: { Authorization: `Basic ${auth}` }
        });
        toast.success('Événement mis à jour!');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/events`, formData, {
          headers: { Authorization: `Basic ${auth}` }
        });
        toast.success('Événement créé!');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchEvents();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet événement?')) return;
    const auth = localStorage.getItem('admin_auth');
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/events/${eventId}`, {
        headers: { Authorization: `Basic ${auth}` }
      });
      toast.success('Événement supprimé!');
      fetchEvents();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openEditDialog = (event) => {
    setEditingEvent(event);
    setFormData(event);
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingEvent(null);
    setFormData({
      id: '',
      title: '',
      subtitle: '',
      date: '',
      time: '',
      venue: '',
      address: '',
      city: '',
      type: 'monthly',
      xceed_url: '',
      poster_url: '',
      status: 'upcoming',
      featured: false
    });
  };

  return (
    <div data-testid="admin-events-page">
      <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl text-[#F5F0E8] mb-1">Événements</h1>
          <p className="text-sm text-[#B9B2A8]">Gérez vos événements (soirées et Open Air)</p>
        </div>
        <div className="flex gap-3">
          <Select value={sortOrder} onValueChange={setSortOrder}>
            <SelectTrigger className="w-56 bg-[#1A1A1A] border-white/10 text-white">
              <SelectValue placeholder="Trier par date" />
            </SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-white/10">
              <SelectItem value="desc" className="text-white">📅 Plus récent en premier</SelectItem>
              <SelectItem value="asc" className="text-white">📅 Plus ancien en premier</SelectItem>
            </SelectContent>
          </Select>
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#C9A84C] text-[#060606] hover:bg-[#E2C56A]">
                <Plus className="w-4 h-4 mr-2" />
                Ajouter
              </Button>
            </DialogTrigger>
          <DialogContent className="bg-[#0B0B0C] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-[#F5F0E8] font-display">
                {editingEvent ? 'Modifier l\'\u00e9vénement' : 'Nouvel \u00e9vénement'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Titre</label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="bg-white/5 border-white/10 text-[#F5F0E8]"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Sous-titre</label>
                  <Input
                    value={formData.subtitle || ''}
                    onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                    className="bg-white/5 border-white/10 text-[#F5F0E8]"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Date</label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="bg-white/5 border-white/10 text-[#F5F0E8]"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Horaires</label>
                  <Input
                    value={formData.time}
                    onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                    placeholder="14:00 - 22:00"
                    className="bg-white/5 border-white/10 text-[#F5F0E8]"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Lieu</label>
                  <Input
                    value={formData.venue}
                    onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
                    className="bg-white/5 border-white/10 text-[#F5F0E8]"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Ville</label>
                  <Input
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="bg-white/5 border-white/10 text-[#F5F0E8]"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Adresse</label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="bg-white/5 border-white/10 text-[#F5F0E8]"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Type</label>
                  <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-[#F5F0E8]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0B0B0C] border-white/10">
                      <SelectItem value="monthly" className="text-[#F5F0E8]">Soirée mensuelle</SelectItem>
                      <SelectItem value="openair" className="text-[#F5F0E8]">Open Air</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Statut</label>
                  <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-[#F5F0E8]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0B0B0C] border-white/10">
                      <SelectItem value="upcoming" className="text-[#F5F0E8]">À venir</SelectItem>
                      <SelectItem value="sold_out" className="text-[#F5F0E8]">Sold Out</SelectItem>
                      <SelectItem value="announced" className="text-[#F5F0E8]">Annoncé</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">URL Xceed (billetterie)</label>
                <Input
                  value={formData.xceed_url}
                  onChange={(e) => setFormData({ ...formData, xceed_url: e.target.value })}
                  className="bg-white/5 border-white/10 text-[#F5F0E8]"
                />
              </div>
              <ImageUploader
                label="Affiche / Flyer"
                value={formData.poster_url}
                onChange={(url) => setFormData({ ...formData, poster_url: url })}
              />

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.featured}
                  onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                  className="w-4 h-4 accent-[#C9A84C]"
                />
                <label className="text-sm text-[#F5F0E8]">\u00c9vénement principal (mis en avant)</label>
              </div>
              <Button onClick={handleSubmit} className="w-full bg-[#C9A84C] text-[#060606] hover:bg-[#E2C56A]">
                {editingEvent ? 'Mettre \u00e0 jour' : 'Créer'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Events List */}
      <div className="space-y-4">
        {events.map((event, index) => (
          <motion.div
            key={event.id || event._id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className={`bg-white/5 border rounded-xl p-4 flex items-center gap-4 ${
              event.featured ? 'border-[#C9A84C]/40' : 'border-white/10'
            }`}
          >
            {event.poster_url && (
              <img src={getImageUrl(event.poster_url)} alt={event.title} className="w-20 h-20 object-cover rounded-lg" />
            )}
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-display text-lg text-[#F5F0E8]">{event.title}</h3>
                {event.featured && <Star className="w-4 h-4 text-[#C9A84C] fill-[#C9A84C]" />}
              </div>
              <div className="flex items-center gap-4 text-xs text-[#B9B2A8] mt-1">
                <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {event.date}</span>
                <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {event.venue}</span>
                <span className={`px-2 py-0.5 rounded ${
                  event.type === 'openair' ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'
                }`}>
                  {event.type === 'openair' ? 'Open Air' : 'Mensuelle'}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={() => openEditDialog(event)} className="text-[#B9B2A8] hover:text-[#F5F0E8]">
                <Pencil className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => handleDelete(event.id)} className="text-[#B9B2A8] hover:text-red-400">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default AdminEventsPage;
